/**
 * Canonical BBG plan limits (mirrors supabase subscription_plans).
 * Shared by Vite client (src) and Vercel serverless (api).
 *
 * 2026-05 pricing update: canonical ids are free / starter / author / pro / trial.
 * Legacy ids (1bog / 3boeger / 31boeger / basic / publisher) are kept as aliases
 * so existing Stripe subscriptions and stored `subscription` values keep working.
 */

export type PlanId = "free" | "trial" | "starter" | "author" | "pro";

export type UsageAction =
  | "book_used"
  | "cover_used"
  | "litra_prompt_used"
  | "book_exported";

export interface PlanLimits {
  id: PlanId;
  name: string;
  booksPerMonth: number;
  coversPerMonth: number;
  litraPromptsPerMonth: number;
  priceMonthlyUsd: number;
  canExport: boolean;
  /** Trial only: hard word cap per book. */
  maxWordsPerBook?: number;
  /** Trial only: how many days the trial lasts. */
  trialDays?: number;
}

export const SUBSCRIPTION_PLANS: Record<PlanId, PlanLimits> = {
  free: {
    id: "free",
    name: "Free",
    booksPerMonth: 1,
    coversPerMonth: 0,
    litraPromptsPerMonth: 10,
    priceMonthlyUsd: 0,
    canExport: false,
  },
  trial: {
    id: "trial",
    name: "Trial",
    booksPerMonth: 1,
    coversPerMonth: 1,
    litraPromptsPerMonth: 50,
    priceMonthlyUsd: 0,
    canExport: true,
    maxWordsPerBook: 10000,
    trialDays: 7,
  },
  starter: {
    id: "starter",
    name: "Starter",
    booksPerMonth: 1,
    coversPerMonth: 3,
    litraPromptsPerMonth: 50,
    priceMonthlyUsd: 11,
    canExport: true,
  },
  author: {
    id: "author",
    name: "Author",
    booksPerMonth: 3,
    coversPerMonth: 18,
    litraPromptsPerMonth: 200,
    priceMonthlyUsd: 29,
    canExport: true,
  },
  pro: {
    id: "pro",
    name: "Pro",
    booksPerMonth: 30,
    coversPerMonth: 180,
    litraPromptsPerMonth: 1650,
    priceMonthlyUsd: 59,
    canExport: true,
  },
};

/**
 * Any historical hint → canonical PlanId.
 * Legacy meaning: basic/1bog = entry, pro/3boeger = mid, publisher/31boeger = top.
 * New meaning:      starter   = entry, author      = mid, pro              = top.
 * So legacy "pro"/"3boeger" map to NEW "author"; legacy "publisher"/"31boeger"
 * (and admin "Publisher") map to NEW "pro" (the top tier).
 */
const PLAN_ALIASES: Record<string, PlanId> = {
  free: "free",
  trial: "trial",
  // entry tier
  starter: "starter",
  basic: "starter",
  "1bog": "starter",
  "1 bog": "starter",
  single: "starter",
  // mid tier
  author: "author",
  "3boeger": "author",
  "3boegeger": "author",
  "3 bøger": "author",
  triple: "author",
  pro: "author", // legacy "pro" was the 3-book mid tier
  // top tier
  publisher: "pro",
  "31boeger": "pro",
  "31boegeger": "pro",
  "31 bøger": "pro",
};

/** Map Stripe / localStorage / Supabase user.subscription to plan id. */
export function planIdFromHint(...hints: Array<string | null | undefined>): PlanId {
  for (const raw of hints) {
    const key = String(raw || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    if (!key) continue;
    if (PLAN_ALIASES[key]) return PLAN_ALIASES[key];
    if (key.includes("publish") || key.includes("31 ")) return "pro";
    if (key.includes("author") || key.includes("3 bog") || key.includes("3 book")) return "author";
    if (key.includes("starter") || key.includes("basic") || key.includes("1 bog") || key.includes("1 book")) return "starter";
    if (key.includes("pro")) return "author"; // legacy "pro" = mid tier
    if (key.includes("trial")) return "trial";
  }
  return "free";
}

/** Resolve any id/alias to a canonical plan record (defaults to free). */
export function resolvePlan(planId: string | null | undefined): PlanLimits {
  const direct = planId ? SUBSCRIPTION_PLANS[planId as PlanId] : undefined;
  if (direct) return direct;
  return SUBSCRIPTION_PLANS[planIdFromHint(planId)] || SUBSCRIPTION_PLANS.free;
}

export function limitForAction(plan: PlanLimits, action: UsageAction): number {
  switch (action) {
    case "book_used":
      return plan.booksPerMonth;
    case "cover_used":
      return plan.coversPerMonth;
    case "litra_prompt_used":
      return plan.litraPromptsPerMonth;
    case "book_exported":
      return plan.canExport ? 999 : 0;
    default:
      return 0;
  }
}

export function calendarMonthPeriod(now = new Date()): { start: string; end: string } {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999));
  return { start: start.toISOString(), end: end.toISOString() };
}

export interface UsageCheckResult {
  allowed: boolean;
  action: UsageAction;
  planId: PlanId;
  currentUsage: number;
  limit: number;
  remaining: number;
  resetsAt: string;
  message?: string;
  bypass?: boolean;
}

export function buildLimitMessage(
  action: UsageAction,
  check: Pick<UsageCheckResult, "limit" | "resetsAt">,
  lang: "DA" | "EN" = "DA",
): string {
  const names: Record<UsageAction, { da: string; en: string }> = {
    book_used: { da: "bøger", en: "books" },
    cover_used: { da: "covers", en: "covers" },
    litra_prompt_used: { da: "LITRA!-beskeder", en: "LITRA! messages" },
    book_exported: { da: "eksporter", en: "exports" },
  };
  const label = lang === "DA" ? names[action].da : names[action].en;
  const date = new Date(check.resetsAt).toLocaleDateString(lang === "DA" ? "da-DK" : "en-US");
  if (lang === "DA") {
    return `Du har nået din månedlige grænse på ${check.limit} ${label}. Grænsen nulstilles ${date}. Opgrader for mere.`;
  }
  return `You have reached your monthly limit of ${check.limit} ${label}. Resets on ${date}. Upgrade for more.`;
}

export type PaidPlanId = "starter" | "author" | "pro";

type LandingPlanPerkSlot = "starter" | "author" | "pro";

const PLAN_PERK_SLOT: Record<string, LandingPlanPerkSlot> = {
  // canonical
  starter: "starter",
  author: "author",
  pro: "pro",
  // legacy aliases
  "1bog": "starter",
  basic: "starter",
  "3boeger": "author",
  "31boeger": "pro",
  publisher: "pro",
};

const LANDING_PLAN_PERKS: Record<
  string,
  Record<LandingPlanPerkSlot, string[]>
> = {
  EN: {
    starter: ["1 book / mo.", "3 covers / mo.", "Full access to all tools", "Export & publish · 100% ownership"],
    author: ["3 books / mo.", "18 covers / mo.", "Full access + Marketing Kit", "4× more LITRA prompts than Starter", "Email support"],
    pro: ["30 books / mo.", "180 covers / mo.", "Full access + 1,650 prompts/mo", "Priority support", "Early access to new features"],
  },
  DA: {
    starter: ["1 bog / md.", "3 covers / md.", "Fuld adgang til alle værktøjer", "Eksportér & udgiv · 100% ejerskab"],
    author: ["3 bøger / md.", "18 covers / md.", "Fuld adgang + Marketing Kit", "4× flere LITRA-prompts end Starter", "Email-support"],
    pro: ["30 bøger / md.", "180 covers / md.", "Fuld adgang + 1.650 prompts/md", "Prioriteret support", "Tidlig adgang til nye funktioner"],
  },
  DE: {
    starter: ["1 Buch / Monat", "3 Cover / Monat", "Voller Zugriff auf alle Tools", "Export & Veröffentlichung · 100% Eigentum"],
    author: ["3 Bücher / Monat", "18 Cover / Monat", "Voller Zugriff + Marketing-Kit", "4× mehr LITRA-Prompts als Starter", "E-Mail-Support"],
    pro: ["30 Bücher / Monat", "180 Cover / Monat", "Voller Zugriff + 1.650 Prompts/Monat", "Priorisierter Support", "Früher Zugang zu neuen Funktionen"],
  },
  ES: {
    starter: ["1 libro / mes", "3 portadas / mes", "Acceso completo a todas las herramientas", "Exporta y publica · 100% de propiedad"],
    author: ["3 libros / mes", "18 portadas / mes", "Acceso completo + Marketing Kit", "4× más prompts de LITRA que Starter", "Soporte por email"],
    pro: ["30 libros / mes", "180 portadas / mes", "Acceso completo + 1.650 prompts/mes", "Soporte prioritario", "Acceso anticipado a nuevas funciones"],
  },
  FR: {
    starter: ["1 livre / mois", "3 couvertures / mois", "Accès complet à tous les outils", "Export & publication · 100% de propriété"],
    author: ["3 livres / mois", "18 couvertures / mois", "Accès complet + Marketing Kit", "4× plus de prompts LITRA que Starter", "Support email"],
    pro: ["30 livres / mois", "180 couvertures / mois", "Accès complet + 1 650 prompts/mois", "Support prioritaire", "Accès anticipé aux nouvelles fonctionnalités"],
  },
  IT: {
    starter: ["1 libro / mese", "3 copertine / mese", "Accesso completo a tutti gli strumenti", "Esporta e pubblica · 100% di proprietà"],
    author: ["3 libri / mese", "18 copertine / mese", "Accesso completo + Marketing Kit", "4× più prompt LITRA rispetto a Starter", "Supporto email"],
    pro: ["30 libri / mese", "180 copertine / mese", "Accesso completo + 1.650 prompt/mese", "Supporto prioritario", "Accesso anticipato alle nuove funzionalità"],
  },
  NL: {
    starter: ["1 boek / maand", "3 omslagen / maand", "Volledige toegang tot alle tools", "Exporteer & publiceer · 100% eigendom"],
    author: ["3 boeken / maand", "18 omslagen / maand", "Volledige toegang + Marketing Kit", "4× meer LITRA-prompts dan Starter", "E-mail support"],
    pro: ["30 boeken / maand", "180 omslagen / maand", "Volledige toegang + 1.650 prompts/maand", "Prioriteitssupport", "Vroege toegang tot nieuwe functies"],
  },
  PL: {
    starter: ["1 książka / mies.", "3 okładki / mies.", "Pełny dostęp do wszystkich narzędzi", "Eksport i publikacja · 100% własności"],
    author: ["3 książki / mies.", "18 okładek / mies.", "Pełny dostęp + Marketing Kit", "4× więcej promptów LITRA niż Starter", "Wsparcie e-mail"],
    pro: ["30 książek / mies.", "180 okładek / mies.", "Pełny dostęp + 1650 promptów/mies.", "Wsparcie priorytetowe", "Wczesny dostęp do nowych funkcji"],
  },
  PT: {
    starter: ["1 livro / mês", "3 capas / mês", "Acesso completo a todas as ferramentas", "Exporte e publique · 100% de propriedade"],
    author: ["3 livros / mês", "18 capas / mês", "Acesso completo + Marketing Kit", "4× mais prompts LITRA que Starter", "Suporte por email"],
    pro: ["30 livros / mês", "180 capas / mês", "Acesso completo + 1.650 prompts/mês", "Suporte prioritário", "Acesso antecipado a novos recursos"],
  },
  SE: {
    starter: ["1 bok / mån", "3 omslag / mån", "Full tillgång till alla verktyg", "Exportera & publicera · 100% ägarskap"],
    author: ["3 böcker / mån", "18 omslag / mån", "Full tillgång + Marketing Kit", "4× fler LITRA-prompts än Starter", "E-postsupport"],
    pro: ["30 böcker / mån", "180 omslag / mån", "Full tillgång + 1.650 prompts/mån", "Prioriterad support", "Tidig tillgång till nya funktioner"],
  },
};

/** Perks shown on landing + plans gate (localized copy). */
export function landingPlanPerks(planId: string, lang = "EN"): string[] {
  const code = String(lang || "EN").toUpperCase();
  const perks = LANDING_PLAN_PERKS[code] || LANDING_PLAN_PERKS.EN;
  const slot = PLAN_PERK_SLOT[planId] || "starter";
  return perks[slot] || perks.starter;
}

const PLAN_DESC_COPY: Record<string, Record<PaidPlanId, string>> = {
  EN: {
    starter: "For first-time authors ready to publish.",
    author: "For authors with a steady publishing rhythm.",
    pro: "For serious self-publishers with high output.",
  },
  DA: {
    starter: "Til dig der vil udgive din første bog.",
    author: "Til forfattere med fast udgivelsestempo.",
    pro: "Til seriøse self-publishers og højt udgivelsestempo.",
  },
};

export function landingPlanDesc(planId: PaidPlanId | string, lang: "EN" | "DA"): string {
  const slot = PLAN_PERK_SLOT[planId] || "starter";
  const table = PLAN_DESC_COPY[lang] || PLAN_DESC_COPY.EN;
  return table[slot];
}

const PLAN_CADENCE_BY_LANG: Record<string, string> = {
  EN: "/mo",
  DA: "/md.",
  DE: "/Monat",
  FR: "/mois",
  IT: "/mese",
  ES: "/mes",
  PT: "/mês",
  NL: "/maand",
  SE: "/mån.",
  PL: "/mies.",
};

/** Short monthly suffix shown next to plan prices (e.g. per-month cadence). */
export function planCadenceForLang(lang: string | null | undefined): string {
  const code = String(lang || "EN").toUpperCase();
  return PLAN_CADENCE_BY_LANG[code] ?? PLAN_CADENCE_BY_LANG.EN;
}

export function evaluateUsage(
  planId: PlanId | string,
  action: UsageAction,
  currentUsage: number,
  periodEnd: string,
  bypass = false,
): UsageCheckResult {
  const plan = resolvePlan(planId);
  const limit = limitForAction(plan, action);
  const allowed = bypass || (limit > 0 && currentUsage < limit);
  return {
    allowed,
    action,
    planId: plan.id,
    currentUsage,
    limit,
    remaining: Math.max(0, limit - currentUsage),
    resetsAt: periodEnd,
    bypass,
    message: allowed ? undefined : buildLimitMessage(action, { limit, resetsAt: periodEnd }),
  };
}
